/**
 * Box 9: Results by Objective (spec §3.3). One row per objective, never a
 * totals row (Result is never summed across objectives). Result reads the cut
 * window; "ไม่ได้รายงาน" when the objective spent and its platforms did not report
 * the metric, "—" when it has no Result or nothing to count in the window (I2). Under each Result and Cost per Result
 * figure a second line names its metric, as D3 does (rules-04 E3). Targets come from the verdict's status (TargetStatus);
 * in all workspaces the target columns give way to one line (R10).
 *
 * @module components/overview/ObjectiveTable
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TONE_CLASS, deltaTone } from '@/services/looker/lookerDelta';
import { TargetStatus, TargetValue, targetHeader } from '@/components/looker/TargetCells';
import { coverageLabelsFrom } from '@/services/looker/lookerCoverageLabels';
import type { TargetObjective } from '@/services/targets/targetRules';
import { reachBadgeShown, type ResultMappingInfo } from '@/services/targets/resultMetric';
import type { ObjectiveRow } from '@/services/overview/objectiveResults';
import type { ObjectiveTable as ObjectiveTableData } from '@/services/overview/overviewView';
import type { OverviewWindows } from '@/services/overview/overviewTypes';
import { fmtCount, fmtDelta, fmtMoney, fmtPct, objectiveText } from '@/services/overview/overviewFormat';
import { Chip, CostFigure, LowSample, OverviewTable, ReachBadge, ResultFigure, type OverviewColumn } from './OverviewTable';
import { ResultFootnotes } from './ResultCards';

interface Props { table: ObjectiveTableData; windows: OverviewWindows; mapping: ResultMappingInfo; currency: string | null }

export const ObjectiveTable: React.FC<Props> = ({ table, windows, mapping, currency }) => {
  const { t } = useTranslation();
  const notes = coverageLabelsFrom(t);
  const { targets } = table;
  const columns: Array<OverviewColumn<ObjectiveRow>> = [
    {
      id: 'objective', header: t('overview.col.objective'), cell: (r) => (
        <>
          {objectiveText(r.objective, t)}
          <LowSample on={r.lowSample} />
          {(table.gaps[r.objective] ?? []).map((g) => <Chip key={g.platform} text={notes.notReported(g.platform, g.metric)} />)}
          <ReachBadge on={reachBadgeShown(r.objective, mapping)} />
        </>
      ),
    },
    { id: 'spend', header: t('overview.metric.spend'), numeric: true, cell: (r) => fmtMoney(r.spend, currency) },
    { id: 'share', header: t('overview.share'), numeric: true, cell: (r) => fmtPct(r.share, 1) },
    { id: 'impressions', header: t('overview.metric.impressions'), numeric: true, cell: (r) => fmtCount(r.impressions) },
    { id: 'clicks', header: t('overview.metric.clicks'), numeric: true, cell: (r) => fmtCount(r.clicks) },
    { id: 'ctr', header: t('overview.metric.ctr'), numeric: true, cell: (r) => fmtPct(r.ctr) },
    { id: 'cpc', header: t('overview.metric.cpc'), numeric: true, cell: (r) => fmtMoney(r.cpc, currency) },
    { id: 'cpm', header: t('overview.metric.cpm'), numeric: true, cell: (r) => fmtMoney(r.cpm, currency) },
    { id: 'result', header: t('overview.result'), numeric: true, cell: (r) => <ResultFigure row={r} /> },
    { id: 'cpr', header: t('overview.costPerResult'), numeric: true, cell: (r) => <CostFigure row={r} currency={currency} /> },
    {
      id: 'cprDelta', header: t('overview.deltaCostPerResult'), numeric: true,
      cell: (r) => <span className={`font-semibold ${TONE_CLASS[deltaTone('cost_per_result', r.costPerResultDelta)]}`}>{fmtDelta(r.costPerResultDelta)}</span>,
    },
    ...(targets ? [
      { id: 'target', header: targetHeader(t, targets), numeric: true, cell: (r: ObjectiveRow) => <TargetValue cell={targets.byObjective[r.objective as TargetObjective]} targets={targets} /> },
      { id: 'targetStatus', header: t('looker.targets.statusColumn'), cell: (r: ObjectiveRow) => <TargetStatus cell={targets.byObjective[r.objective as TargetObjective]} targets={targets} /> },
    ] : []),
  ];
  return (
    <div>
      <OverviewTable columns={columns} rows={table.rows} rowKey={(r) => r.objective} faded={(r) => r.lowSample} />
      {!targets && <p className="mt-2 text-xs text-neutral-700">{t('overview.targetsNeedWorkspace')}</p>}
      <ResultFootnotes windows={windows} mapping={mapping} awarenessShown={table.rows.some((r) => r.objective === 'awareness')} card={false} />
    </div>
  );
};
